import { Container, Spacer } from "@components/atoms";
import Loader from "@components/atoms/Loader";
import LayoutContainer from "@components/layout/LayoutContainer";
import { ToastTypes, showToast } from "@components/molecules/VDBToast/util";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  BulkRequestEnum,
  ProductCategory,
  ProductTypeCommon,
} from "@utils/constants";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { navigate } from "@navigations/utils/RootNavigation";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { Props } from "./types";
import PdpHeader from "./components/PdpHeader";
import PdpGallery from "./components/PdpGallery";
import PdpFooter from "./components/PdpFooter";
import PdpBottomActions from "./components/PdpBottomActions";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { usePdpSectionLayouts } from "./hooks/usePdpSectionLayouts";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { getProductThemeFromType } from "@components/common/Header/utils";
import {
  isGemstone,
  isJewelry,
  isLabGrown,
  isPair,
  isParcel,
} from "@utils/product/productValidators";
import { useAddToCart, useCheckItemAvailability } from "@apis/cartRequest";
import { useSelector } from "react-redux";
import Animated from "react-native-reanimated";
import { checkAllFieldsEntered, useProductInfo } from "./utils/utils";
import {
  useAppSettings,
  useSupplierSpecificCommentInOrangeMode,
} from "@redux/slices/globalSlice";
import { useIsPublicAccessRestricted } from "@utils/usePrivateAppGuard";
import { getProductProperty } from "@scenes/findMatch/Utils/ProductDetailList/utils";
import StickyPriceContainer from "./utils/StickyPriceContainer";
import GoBackComponent from "@components/molecules/GoBackComponent/GoBackComponent";
import Breadcrumbs from "@components/molecules/Breadcrumbs/Breadcrumbs";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import PdpBottomButtons from "./utils/PdpBottomButtons";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";
import StickyPriceWrapper from "./utils/StickyPriceWrapper";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { KeyboardAwareScrollView as KCKeyboardAwareScrollView } from "react-native-keyboard-controller";
import Routes from "@navigations/Routes";
import { usePdpModals } from "./hooks/usePdpModals";
import { usePdpScrollState } from "./hooks/usePdpScrollState";
import { usePdpKeyboardState } from "./hooks/usePdpKeyboardState";
import { usePdpMediaState } from "./hooks/usePdpMediaState";
import { usePdpLayoutState } from "./hooks/usePdpLayoutState";
import { usePdpData } from "./hooks/usePdpData";
import { usePdpPricing } from "./hooks/usePdpPricing";
import { usePdpButtons } from "./hooks/usePdpButtons";
import { usePdpAvailability } from "./hooks/usePdpAvailability";
import { usePdpVisibility } from "./hooks/usePdpVisibility";
import { usePdpUIState } from "./hooks/usePdpUIState";
import { usePdpNavigation } from "./hooks/usePdpNavigation";
import { usePdpFocusEffects } from "./hooks/usePdpFocusEffects";
import { usePdpComputedValues } from "./hooks/usePdpComputedValues";
import { usePdpComponentRenderer } from "./hooks/usePdpComponentRenderer";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useGetPdpSectionsOrder } from "../../hooks/useGetSectionLayout";
import { getVisiblePdpSections } from "./utils/pdpConfigurableComponent";
import { css } from "styled-components/native";

const shouldShowJewelryComponents = (itemDetail: any): boolean => {
  if (itemDetail?.has_components) {
    return true;
  }

  const jewelryType =
    itemDetail?.additional_attributes?.raw_data?.["Jewelry Types"];
  return jewelryType === "Components";
};

const PDPScreen = ({ route }: Props) => {
  const navigation = useNavigation();
  const { modals, setModals } = usePdpModals();

  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  // Use container dimensions if provided (modal case), otherwise use window dimensions
  const isModal = route?.isModal ?? false;
  const fromReviewPage = route?.fromReviewPage ?? false;
  const userInfo = useUserInfo();
  let enterSecondFlow = route?.params?.enterSecondFlow;
  enterSecondFlow = normalizeBoolean(enterSecondFlow);
  const fromRequest = route?.params?.fromRequest;
  const fromOrderCart = route?.params?.fromOrderCart;
  const product_type = route?.params?.productType as ProductCategory;
  const componentData = route?.params?.componentData;
  const fromHomePage =
    route?.params?.fromHomePage === "true" ||
    route?.params?.fromHomePage === true;

  const isFocused = useIsFocused();
  const theme = useTheme();
  const additionalAttributes = useMemo(
    () => JSON.parse(route?.params?.additionalAttributes || "{}"),
    [route?.params?.additionalAttributes]
  );

  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const { t } = useTranslation();
  const typography = useTypographyStore();

  const { selectedGroups } = useSearchGroupSlice();

  const { isKeyboardVisible, isMultilineFocused } = usePdpKeyboardState();

  const stoneToSettingFlow =
    enterSecondFlow &&
    (product_type === ProductCategory.JEWELRY ||
      product_type === ProductCategory.LABGROWN_JEWELRY);

  const {
    setDefaultSlug,
    setUserSelectedSlug,
    appendNewVariation,
    setIsVariationRequired,
    isVariationRequired,
    setCustomDesignConfiguration,
    itemDetail: mItem,
    setItemDetail,
    setStoneItemDetail,
    matchingPairItemDetail,
    isAPILoading,
    updateJcSelectedOptions,
    allTypes,
    jcSelectedOption,
    personalization,
    setPersonalization,
    setAllTypes,
    setMatchingPairItemDetail,
    updateJewelryConfigInfo,
    stoneItemDetail: mStoneItemDetail,
    matchingBandData,
    resetDynamicParams,
  } = useJewelryConfiguratorState();

  const checkItemAvailability = useCheckItemAvailability();

  const { settings, enableJewelryBuilder } = useOrgSettingsSlice();
  let mGroupIds = selectedGroups[product_type];

  const {
    findMatchSectionLayout,
    priceDisclaimerLayout,
    mediaSectionLayout,
    commentsLayout,
    supplierSectionLayout,
    personalizationSectionLayout,
    refundPolicyLayout,
  } = usePdpSectionLayouts({
    product_type,
  });
  const isGuest = useSelector((state) => state.userInfo.isGuest);
  const renderShowErrorComponent = () => {
    showToast(
      t("productDetailPage.notFoundTitle.text", "Data Issue"),
      t(
        "productDetailPage.itemNotFound.text",
        "Item is currently not available."
      ),
      ToastTypes.ERROR,
      true,
      true
    );
    if (Platform.OS === "web") {
      localStorage.removeItem("jcSelectedOption");
      localStorage.removeItem("personalization");
    }
    // if (!isModal || !route?.params?.fromComponent) {
    //   navigation.goBack();
    // }
    navigation.goBack();
  };

  const showLabGrownPopUp = () => {
    if (!modals.showLabgrownPopup && fromRequestDetailsScreen) {
      if (isLabGrown(product_type) && userInfo.showLabGrownPopup()) {
        showToast("", userInfo.getLabGrownText(), ToastTypes.INFO, true, true);
        toggleModal("showLabgrownPopup", true);
      }

      if (
        isJewelry(product_type) &&
        getAppSettings.displayLabGrownJewelryToast
      ) {
        const data = getDiamondDetailData.data?.data;
        if (
          data?.response?.body?.jewelries[0]?.sub_type ===
          ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
        ) {
          showToast(
            "",
            t("global.toast.labgrownjewelry.title"),
            ToastTypes.LABGROWN_JEWELRY,
            true,
            true
          );
          toggleModal("showLabgrownPopup", true);
        }
      }
    }
  };

  const { priceMode, priceMarkup } = usePdpPricing({
    route,
    userInfo,
  });

  const { showAvailabilityDot, isStockNumberVisible, showMatchingBandSection } =
    usePdpAvailability({
      product_type,
      priceMode,
    });

  const {
    getDiamondDetailData,
    getMatchPairDetailData,
    componentsData,
    componentsLoading,
    componentsFetching,
    getAddToJewelryOptionsData,
    itemDetail,
    reportBadMedia,
    removeBreadCrumbFromHistory,
  } = usePdpData({
    route,
    isFocused,
    isModal,
    product_type,
    priceMode,
    priceMarkup,
    userInfo,
    mGroupIds,
    isCustomApp,
    additionalAttributes,
    componentData,
    isVariationRequired,
    findMatchSectionLayout,
    shouldShowJewelryComponents,
    enableJewelryBuilder,
    enterSecondFlow,
    mItem,
    mStoneItemDetail,
    matchingPairItemDetail,
    setItemDetail,
    setStoneItemDetail,
    setMatchingPairItemDetail,
    appendNewVariation,
    setAllTypes,
    setIsVariationRequired,
    setDefaultSlug,
    setUserSelectedSlug,
    setCustomDesignConfiguration,
    updateJcSelectedOptions,
    jcSelectedOption,
    setPersonalization,
    renderShowErrorComponent,
    showLabGrownPopUp,
    fromReviewPage,
    resetDynamicParams,
  });

  // Memoize sticky container height to prevent unnecessary hook re-runs
  const stickyContainerHeight = useMemo(
    () =>
      theme.isMobile
        ? itemDetail?.display_configuration_v2
          ? 250 + (1 + 8 + 20 + 8 + 1 + 8 + 20 + 8) //250 for image, 1 for border, 8 for padding, 20 for title
          : 88.5
        : priceMode === PriceModes.GREEN ||
          itemDetail?.custom_design_configuration
        ? 20 + 8 + 20 + 16 + 1 + 1 + 16 + 16 + 16 // 20 is the height of the stock hash, 20 for short title, 8px are paddings between section, 16px is padding around section, 16px is the padding below section, 16px is the padding above.
        : 20 + 8 + 20 + 8 + 16.5 + 16 + 1 + 1 + 16 + 16 + 16, // 20 is the height of the stock hash, 20 for short title, 16.5 for total price,  8px are paddings between section, 16px is padding around section, 16px is the padding below section, 16px is the padding above.
    [
      theme.isMobile,
      itemDetail?.display_configuration_v2,
      priceMode,
      itemDetail?.custom_design_configuration,
    ]
  );

  const {
    showProductHeaderStickySummary,
    scrollViewRef,
    headerProductView,
    headerStickySummaryViewRef,
    maxContentLength,
    onScroll,
    scrollToTop,
  } = usePdpScrollState({ stickyContainerHeight });

  const {
    width,
    height,
    screenWidth,
    screenHeight,
    contentWidth,
    rightComponentWidth,
    sizes,
    handleLayout,
    handleLayoutPDP,
  } = usePdpLayoutState({
    route,
  });

  const disabledMemo = itemDetail?.vendor_accepts_memo !== true;
  const getAppSettings = useAppSettings();
  const { shouldHide: isRestrictedPublicUser } = useIsPublicAccessRestricted();
  const fromRequestDetailsScreen =
    typeof route?.params?.fromRequestDetailsScreen === "boolean"
      ? route?.params?.fromRequestDetailsScreen
      : typeof route?.params?.fromDeepLink === "string"
      ? JSON.parse(route?.params?.fromDeepLink)
      : false;
  const { productName } = useProductInfo(
    t,
    product_type,
    itemDetail,
    isCustomApp
  );
  const addToCart = useAddToCart(BulkRequestEnum.FromPdp);

  const enableBmhRetailMode = useSelector(
    (state: any) => state.globalData?.app_settings?.enableBmhRetailMode ?? false
  );

  const supplierSpecificCommentInOrangeMode =
    useSupplierSpecificCommentInOrangeMode();

  // Set up navigation options
  usePdpNavigation({
    itemDetail,
    product_type,
    priceMode,
  });

  const { bottomButtons, requestOptionsButton } = usePdpButtons({
    product_type,
    priceMode,
    itemDetail,
    isCustomApp,
    enableJewelryBuilder,
    enterSecondFlow,
    getAddToJewelryOptionsData,
  });

  const insets = useSafeAreaInsets();

  const {
    addToJewelryMenu,
    setAddToJewelryMenu,
    selectedTabSlideSheet,
    setSelectedTabSlideSheet,
    customOptions,
    itemQuantity,
    setItemQuantity,
  } = usePdpUIState({
    mediaSectionLayout,
    priceMode,
  });

  // Get computed values (now that customOptions is available)
  const {
    isEarring,
    productColor,
    productAlphaColor,
    isCertificateShow,
    allowBreadcrumbByProduct,
    showBreadCrumbInMobile,
    LinkedPairComments,
    renderersProps,
  } = usePdpComputedValues({
    product_type,
    itemDetail,
    isModal,
    enableJewelryBuilder,
    enterSecondFlow,
    mItem,
    mStoneItemDetail,
    customOptions,
    isStockNumberVisible,
    getAddToJewelryOptionsData,
  });

  const pdpSectionsConfig = useGetPdpSectionsOrder(
    product_type,
    isPair(itemDetail)
      ? SearchFilterTypeEnum.PAIRS
      : isParcel(itemDetail)
      ? SearchFilterTypeEnum.PARCEL
      : SearchFilterTypeEnum.SINGLE_STONES
  );

  const visibleSections = useMemo(
    () => getVisiblePdpSections(pdpSectionsConfig, priceMode),
    [pdpSectionsConfig, priceMode]
  );

  const {
    showCommentSection,
    showPersonalizationSection,
    showSupplierDetails,
    shouldShowMatchPairButton,
    showOwnerInfo,
  } = usePdpVisibility({
    product_type,
    priceMode,
    itemDetail,
    commentsLayout,
    personalizationSectionLayout,
    supplierSectionLayout,
    findMatchSectionLayout,
    enableJewelryBuilder,
    enterSecondFlow,
    isEarring,
    visibleSections,
  });

  const {
    mediaActiveIndex,
    setMediaActiveIndex,
    stickyHeaderActiveIndex,
    setStickyHeaderActiveIndex,
    receivedDiamondData,
  } = usePdpMediaState({
    itemDetail,
    product_type,
    isModal,
    componentData,
    getDiamondDetailData,
    isCertificateShow,
  });

  // Tracks which media_ids have already triggered the bad-image API — prevents
  // duplicate API calls when the same media item's error fires more than once.
  const reportedBadMediaIds = useRef<Set<number>>(new Set());

  // Snapshot of the initial media count (excluding dynamically-added 3D items
  // which have no id and never trigger handleError), set once on first data load.
  const originalMediaCountRef = useRef<number | null>(null);

  const hasShownItemUnavailableToast = useRef(false);

  useEffect(() => {
    if (
      !getDiamondDetailData.isFetched ||
      !itemDetail ||
      Object.keys(itemDetail).length === 0 ||
      originalMediaCountRef.current !== null
    )
      return;

    // Only count items that carry an id — dynamically injected 3D entries don't
    // have one and are never reported via the bad-image API.
    const countable = (receivedDiamondData ?? []).filter(
      (item: any) => item.id != null
    );
    originalMediaCountRef.current = countable.length;
  }, [getDiamondDetailData.isFetched, itemDetail?.media]);

  const handleRemoveMedia = useCallback(
    (index: number, media_id: number) => {
      const itemToRemove = receivedDiamondData?.[index];

      // Deduplicate: only call reportBadMedia once per media_id regardless of
      // how many times the image error event fires for the same item.
      if (!reportedBadMediaIds.current.has(media_id)) {
        reportedBadMediaIds.current.add(media_id);
        reportBadMedia(media_id);

        const total = originalMediaCountRef.current;
        if (
          isJewelry(product_type) &&
          total !== null &&
          total > 0 &&
          reportedBadMediaIds.current.size >= total &&
          !hasShownItemUnavailableToast.current
        ) {
          hasShownItemUnavailableToast.current = true;
          showToast(
            "",
            t(
              "productDetailPage.itemNoLongerAvailable.text",
              "This item is no longer available."
            ),
            ToastTypes.ERROR,
            true,
            true,
            undefined,
            undefined,
            true,
            undefined,
            false,
            undefined,
            undefined,
            undefined,
            () => navigation.goBack()
          );
        }
      }

      if (!itemDetail?.media || itemToRemove == null) return;
      setItemDetail({
        ...itemDetail,
        media: itemDetail.media.filter((m: any) => m !== itemToRemove),
      });
    },
    [itemDetail, receivedDiamondData, setItemDetail]
  );

  // Handle focus effects (scroll to top, emit events, refetch queries)
  usePdpFocusEffects({
    isModal,
    productType: route?.params?.productType,
    scrollToTop,
  });

  // In stone-to-setting flow the supported_shapes UI is hidden, so it is never
  // set via user interaction. Backfill option_value from the stone's own shape
  // so the field is present in the buy-request config payload sent to the server.
  useEffect(() => {
    if (!enterSecondFlow || !mStoneItemDetail?.shape || allTypes.length === 0) {
      return;
    }
    if (jcSelectedOption.current.option_value.supported_shapes) {
      return;
    }
    const supportedShapesType = allTypes.find(
      (item) => item.option_type === "supported_shapes"
    );
    const stoneShape = mStoneItemDetail.shape?.toLowerCase();
    const resolvedShapeValue = supportedShapesType?.option_value?.find(
      (item) =>
        item.slug === stoneShape?.replaceAll(" ", "-") ||
        item.value?.toLowerCase() === stoneShape
    )?.value;
    if (resolvedShapeValue) {
      jcSelectedOption.current.option_value.supported_shapes =
        resolvedShapeValue;
    }
  }, [enterSecondFlow, mStoneItemDetail?.shape, allTypes]);

  const textComment = useMemo(() => {
    return getProductProperty(itemDetail?.item_comment, "") || "";
  }, [itemDetail?.item_comment, priceMode]);

  const stoneToSettingPopupSeenRef = useRef(false);
  const [stoneToSettingPopupDismissed, setStoneToSettingPopupDismissed] =
    useState(false);
  const pendingDropdownCommitRef = useRef<(() => void) | null>(null);
  const pendingDropdownRevertRef = useRef<(() => void) | null>(null);

  const onStoneToSettingPopupDismiss = useCallback(() => {
    pendingDropdownCommitRef.current?.();
    pendingDropdownCommitRef.current = null;
    pendingDropdownRevertRef.current = null;
    stoneToSettingPopupSeenRef.current = true;
    setStoneToSettingPopupDismissed(true);
    setModals((prev) => ({ ...prev, showStoneToSettingPopup: false }));
  }, []);

  const onStoneToSettingPopupCancel = useCallback(() => {
    pendingDropdownRevertRef.current?.();
    pendingDropdownRevertRef.current = null;
    pendingDropdownCommitRef.current = null;
    setModals((prev) => ({ ...prev, showStoneToSettingPopup: false }));
  }, []);

  const renderPdpComponent = usePdpComponentRenderer({ renderersProps });

  // Use container dimensions for modal, otherwise use window dimensions
  const isAdaptiveContainerFragment =
    (theme.isMobile || theme.isTablet) && Platform.OS !== "web";
  const AdaptiveContainer = isAdaptiveContainerFragment
    ? React.Fragment
    : KeyboardAwareScrollView;
  const adaptiveContainerProps = isAdaptiveContainerFragment
    ? {}
    : {
        ref:
          theme.isTablet && Platform.OS !== "web" ? undefined : scrollViewRef,
        enableOnAndroid: true,
        extraScrollHeight: Platform.OS === "android" ? 50 : 0,
        keyboardShouldPersistTaps: "handled" as const,
        enableAutomaticScroll: true,
        enableResetScrollToCoords: false,
        contentContainerStyle: { flexGrow: 1 },
        scrollEventThrottle: 16,
        extraHeight: theme.isMobile ? 75 : 0,
        style: {
          flex: 1,
          backgroundColor: theme["core-02"],
          ...(showBreadCrumbInMobile && { paddingTop: 50 }),
        },
        onScroll: theme.isLargeScreen && isModal ? undefined : onScroll,
      };

  const AdaptiveContainerTablet =
    (theme.isMobile || theme.isTablet) && Platform.OS !== "web"
      ? KCKeyboardAwareScrollView
      : Animated.ScrollView;

  const {
    BottomButtonView,
    handleBottomButtonActions,
    handleOpenAskSupplier,
    handleOpen,
    toggleModal,
    isShopifyCartLoading,
  } = PdpBottomButtons({
    bottomButtons,
    route,
    product_type,
    disabledMemo,
    addToJewelryMenu,
    setAddToJewelryMenu,
    getAddToJewelryOptionsData:
      product_type !== ProductCategory.JEWELRY &&
      product_type !== ProductCategory.LABGROWN_JEWELRY &&
      enterSecondFlow === false
        ? getAddToJewelryOptionsData
        : {},
    setModals,
    setSelectedTabSlideSheet,
    priceMode,
    checkAllFieldsEntered,
    addToCart,
    itemQuantity,
    enterSecondFlow,
    fromReviewPage: false,
    enableJewelryBuilder: enableJewelryBuilder,
    itemDetail,
    mStoneItemDetail,
    fromRequest,
    fromOrderCart,
    haveMatchPair:
      !getMatchPairDetailData.isFetching &&
      getMatchPairDetailData?.data?.data?.diamonds?.length !== 0,
    checkItemAvailability,
    stoneToSettingPopupDismissed,
  });

  return (
    <>
      {isJewelry(product_type) &&
      shouldShowJewelryComponents(itemDetail) &&
      (componentsLoading || componentsFetching) ? (
        <Loader />
      ) : Object.keys(itemDetail ?? {})?.length > 0 &&
        (getDiamondDetailData.isFetched ||
          (isModal && route?.params?.fromComponent && componentData)) ? (
        <View
          style={{
            width: "100%",
            flex: 1,
            alignSelf: "center",
            backgroundColor: userInfo?.isIFrame()
              ? "transparent"
              : theme[`${getProductThemeFromType(product_type)}-01`],
          }}
        >
          {theme.isLargeScreen && !isModal && !isRestrictedPublicUser && (
            <StyledWebPageHeader
              isIFrame={userInfo?.isIFrame()}
              style={{ pointerEvents: "auto" }}
            >
              {!enableJewelryBuilder ||
              history.length <= 1 ||
              !allowBreadcrumbByProduct ||
              (userInfo.companyIdentifierEnabled() &&
                itemDetail?.company_identifier) ? (
                <GoBackComponent
                  title={t("productDetailPage.goBack.text")}
                  action={() => {
                    removeBreadCrumbFromHistory();
                    if (fromHomePage) {
                      navigate(Routes.HomeNavigator, {
                        screen: Routes.HomeScreen,
                      });
                    } else if (navigation.canGoBack()) {
                      navigation.goBack();
                    } else {
                      navigate(Routes.AppNavigator, {
                        screen: Routes.ExploreNavigator,
                        params: { screen: Routes.Explore },
                      });
                    }
                  }}
                />
              ) : (
                <></>
              )}
            </StyledWebPageHeader>
          )}
          <LayoutContainer showAsPopover={isModal}>
            {showBreadCrumbInMobile && (
              <BreadCrumbContainerMobile>
                <Breadcrumbs />
              </BreadCrumbContainerMobile>
            )}
            {theme.isMobile &&
            !(isModal && route?.params?.fromComponent) &&
            !isKeyboardVisible ? (
              <StickyPriceWrapper
                ref={headerStickySummaryViewRef}
                show={showProductHeaderStickySummary}
                topMargin={showBreadCrumbInMobile ? 48 : 0}
              >
                <StickyPriceContainer
                  isGuest={isGuest}
                  theme={theme}
                  productColor={productColor}
                  itemDetail={itemDetail}
                  priceMode={priceMode}
                  productType={product_type}
                  showAvailabilityDot={showAvailabilityDot}
                  productName={productName}
                  productAlphaColor={productAlphaColor}
                  activeIndex={mediaActiveIndex}
                  showProductHeaderStickySummary={
                    showProductHeaderStickySummary
                  }
                  setStickyHeaderActiveIndex={setStickyHeaderActiveIndex}
                />
              </StickyPriceWrapper>
            ) : null}
            {!isModal &&
            enableJewelryBuilder &&
            allowBreadcrumbByProduct &&
            theme.isTablet ? (
              !(
                itemDetail?.company_identifier &&
                userInfo.companyIdentifierEnabled()
              ) && (
                <View
                  style={{
                    backgroundColor: theme["core-01"],
                    borderBottomColor: theme["core-03"],
                    borderBottomWidth: 1,
                  }}
                >
                  <Breadcrumbs />
                </View>
              )
            ) : (
              <></>
            )}
            <AdaptiveContainer {...adaptiveContainerProps}>
              <MainContainer>
                <Container
                  maxWidth={
                    theme.isTablet && screenHeight < 775
                      ? 900
                      : theme.isLargeScreen && height > 720
                      ? maxContentLength
                      : 1008
                  }
                  noPadding
                >
                  {!isModal &&
                  enableJewelryBuilder &&
                  theme.isLargeScreen &&
                  allowBreadcrumbByProduct
                    ? !(
                        itemDetail?.company_identifier &&
                        userInfo.companyIdentifierEnabled()
                      ) && <Breadcrumbs />
                    : null}
                  <ViewContainer
                    onLayout={handleLayout}
                    isGem={isGemstone(product_type)}
                  >
                    <RowViews>
                      <CenterView>
                        <RowView>
                          {!theme.isMobile && (
                            <PdpGallery
                              receivedDiamondData={receivedDiamondData}
                              itemDetail={itemDetail}
                              productType={product_type}
                              fromComponent={route?.params?.fromComponent}
                              screenWidth={screenWidth}
                              contentWidth={contentWidth}
                              sizes={sizes}
                              onRemoveMedia={handleRemoveMedia}
                              shouldShowJewelryComponents={
                                shouldShowJewelryComponents
                              }
                              isModal={isModal}
                              fromReviewPage={fromReviewPage}
                            />
                          )}
                          <RowContainer>
                            <>
                              <WidthContainer>
                                {!theme.isMobile &&
                                !(isModal && route?.params?.fromComponent) &&
                                !isKeyboardVisible ? (
                                  <FixedSetPriceView>
                                    <FixedContentGalleryViewRight
                                      ref={headerStickySummaryViewRef}
                                    >
                                      <ParentStickyPriceContainer
                                        showProductHeaderStickySummary={
                                          showProductHeaderStickySummary
                                        }
                                        height={stickyContainerHeight}
                                      >
                                        <StickyPriceContainer
                                          isGuest={isGuest}
                                          theme={theme}
                                          productColor={productColor}
                                          itemDetail={itemDetail}
                                          priceMode={priceMode}
                                          productType={product_type}
                                          showAvailabilityDot={
                                            showAvailabilityDot
                                          }
                                          productName={productName}
                                          productAlphaColor={productAlphaColor}
                                        />
                                      </ParentStickyPriceContainer>
                                    </FixedContentGalleryViewRight>
                                  </FixedSetPriceView>
                                ) : null}
                                <AdaptiveContainerTablet
                                  bottomOffset={isMultilineFocused ? 130 : 90}
                                  keyboardShouldPersistTaps="handled"
                                  onScroll={onScroll}
                                  scrollEventThrottle={16}
                                  removeClippedSubviews={
                                    Platform.OS === "android"
                                      ? false
                                      : undefined
                                  }
                                  contentContainerStyle={
                                    Platform.OS === "android" &&
                                    (theme.isMobile || theme.isTablet)
                                      ? {
                                          paddingBottom:
                                            insets.bottom +
                                            SpaceType.ExtraLarge,
                                        }
                                      : undefined
                                  }
                                  style={{
                                    ...(showBreadCrumbInMobile && {
                                      paddingTop: 50,
                                    }),
                                  }}
                                >
                                  {theme.isMobile && (
                                    <PdpGallery
                                      receivedDiamondData={receivedDiamondData}
                                      itemDetail={itemDetail}
                                      productType={product_type}
                                      fromComponent={
                                        route?.params?.fromComponent
                                      }
                                      screenWidth={screenWidth}
                                      contentWidth={contentWidth}
                                      sizes={sizes}
                                      setMediaActiveIndex={setMediaActiveIndex}
                                      mediaActiveIndex={stickyHeaderActiveIndex}
                                      onRemoveMedia={handleRemoveMedia}
                                      shouldShowJewelryComponents={
                                        shouldShowJewelryComponents
                                      }
                                      isModal={isModal}
                                      fromReviewPage={fromReviewPage}
                                    />
                                  )}

                                  <PdpHeader
                                    itemDetail={itemDetail}
                                    productType={product_type}
                                    priceMode={priceMode}
                                    productColor={productColor}
                                    productAlphaColor={productAlphaColor}
                                    productName={productName}
                                    showAvailabilityDot={showAvailabilityDot}
                                    isModal={isModal}
                                    isGuest={isGuest}
                                    fromComponent={route?.params?.fromComponent}
                                    sizes={sizes}
                                    onLayout={handleLayoutPDP}
                                    {...(enableJewelryBuilder &&
                                      theme.isMobile && {
                                        ref: headerProductView,
                                      })}
                                  />

                                  {visibleSections.map(
                                    (componentName: string, index: number) => {
                                      const component = renderPdpComponent({
                                        componentName,
                                        t: t as any,
                                        theme,
                                        typography,
                                        sizes,
                                        width,
                                        product_type,
                                        priceMode,
                                        itemDetail,
                                        isModal,
                                        isCustomApp,
                                        userInfo,
                                        priceDisclaimerLayout,
                                        findMatchSectionLayout,
                                        personalizationSectionLayout,
                                        commentsLayout,
                                        showPersonalizationSection,
                                        showCommentSection,
                                        showSupplierDetails,
                                        shouldShowMatchPairButton,
                                        showOwnerInfo,
                                        LinkedPairComments,
                                        textComment,
                                        productColor,
                                        allTypes,
                                        rightComponentWidth,
                                        enterSecondFlow,
                                        updateJewelryConfigInfo:
                                          updateJewelryConfigInfo as any,
                                        shouldShowJewelryComponents,
                                        onDropdownSelect: stoneToSettingFlow
                                          ? (
                                              initialValue: string,
                                              currentValue: string,
                                              commitFn?: () => void,
                                              revertFn?: () => void
                                            ) => {
                                              if (
                                                currentValue === initialValue
                                              ) {
                                                commitFn?.();
                                                stoneToSettingPopupSeenRef.current =
                                                  false;
                                                setStoneToSettingPopupDismissed(
                                                  false
                                                );
                                                pendingDropdownCommitRef.current =
                                                  null;
                                                pendingDropdownRevertRef.current =
                                                  null;
                                                setModals((prev) => ({
                                                  ...prev,
                                                  showStoneToSettingPopup:
                                                    false,
                                                }));
                                              } else if (
                                                !stoneToSettingPopupSeenRef.current
                                              ) {
                                                pendingDropdownCommitRef.current =
                                                  commitFn ?? null;
                                                pendingDropdownRevertRef.current =
                                                  revertFn ?? null;
                                                setModals((prev) => ({
                                                  ...prev,
                                                  showStoneToSettingPopup: true,
                                                }));
                                              } else {
                                                commitFn?.();
                                              }
                                            }
                                          : undefined,
                                        componentsData,
                                        headerProductView,
                                        handleLayout,
                                        getMatchPairDetailData,
                                        showMatchingBandSection,
                                        matchingBandData,
                                        showSupplierSpecificCommentInOrangeMode:
                                          supplierSpecificCommentInOrangeMode as boolean,
                                        refundPolicyLayout:
                                          refundPolicyLayout as any,
                                      });
                                      if (!component) return null;
                                      return (
                                        <React.Fragment key={componentName}>
                                          {component}
                                          {index <
                                            visibleSections.length - 1 && (
                                            <Spacer size={sizes.spacing} />
                                          )}
                                        </React.Fragment>
                                      );
                                    }
                                  )}
                                </AdaptiveContainerTablet>
                              </WidthContainer>
                            </>
                          </RowContainer>
                        </RowView>
                      </CenterView>
                    </RowViews>
                  </ViewContainer>
                </Container>
                <PdpBottomActions
                  itemDetail={itemDetail}
                  productType={product_type}
                  priceMode={priceMode}
                  modals={modals}
                  toggleModal={toggleModal}
                  onStoneToSettingPopupDismiss={onStoneToSettingPopupDismiss}
                  onStoneToSettingPopupCancel={onStoneToSettingPopupCancel}
                  selectedTabSlideSheet={selectedTabSlideSheet}
                  itemQuantity={itemQuantity}
                  setItemQuantity={setItemQuantity}
                  jcSelectedOption={jcSelectedOption}
                  personalization={personalization}
                  requestOptionsButton={requestOptionsButton}
                  handleBottomButtonActions={handleBottomButtonActions}
                  settings={settings}
                  userInfo={userInfo}
                />
              </MainContainer>
            </AdaptiveContainer>
            <PdpFooter
              itemDetail={itemDetail}
              productType={product_type}
              priceMode={priceMode}
              isCustomApp={isCustomApp}
              isModal={isModal}
              isGuest={isGuest}
              fromComponent={route?.params?.fromComponent}
              itemQuantity={itemQuantity}
              setItemQuantity={setItemQuantity}
              bottomButtons={bottomButtons}
              isAPILoading={isAPILoading}
              addToCartLoading={addToCart.isLoading}
              isShopifyCartLoading={isShopifyCartLoading}
              screenHeight={screenHeight}
              height={height}
              maxContentLength={maxContentLength}
              insets={insets}
              BottomButtonView={BottomButtonView}
              handleBottomButtonActions={handleBottomButtonActions}
              handleOpen={handleOpen}
              handleOpenAskSupplier={handleOpenAskSupplier}
              userInfo={userInfo}
              enableBmhRetailMode={enableBmhRetailMode}
            />
          </LayoutContainer>
        </View>
      ) : getDiamondDetailData.isFetching ||
        getDiamondDetailData.isLoading ||
        Object.keys(itemDetail ?? {})?.length === 0 ? (
        <Loader />
      ) : null}
    </>
  );
};

//TODO Remove the loader condition for jewelry components after server improvement for API load time is done
// (isJewelry(product_type) &&
// shouldShowJewelryComponents(itemDetail) &&
// (componentsLoading || componentsFetching))

const WidthContainer = styled.View`
  width: 100%;
`;

const FixedSetPriceView = styled.View`
  width: 100%;
  align-self: flex-end;
  position: sticky;
  top: 0;
  flex-shrink: ${({ theme }) => (theme.isLargeScreen ? 1 : 0)};
  z-index: 1;
`;

const CenterView = styled.View`
  width: 100%;
`;

const RowViews = styled.View`
  align-items: center;
`;

const RowView = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  gap: ${({ theme }) => (!theme.isMobile ? 30 : 0)}px;
`;

const ViewContainer = styled.View<{ isGem: boolean }>`
  padding-left: 0px;
  padding-right: 0px;
  padding-bottom: ${({ isGem }) => (isGem ? 8 : 0)}px;
`;

const FixedContentGalleryViewRight = styled.View<any>`
  flex: 1;
`;

const MainContainer = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme["core-02"]};
  align-content: center;
  align-self: center;
  width: ${({ theme }) => (theme.isLargeScreen ? "80%" : "100%")};
`;
const RowContainer = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  flex: 1;
  flex-basis: auto;
  width: ${({ theme }) => (theme.isMobile ? "100%" : "50%")};
`;

const ParentStickyPriceContainer = styled.View<{
  showProductHeaderStickySummary: boolean;
  height: number;
}>`
  width: 100%;
  height: ${({ height }) => height}px;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Double}px;
  background-color: ${({ theme }) => theme["core-02"]};
  display: ${({ showProductHeaderStickySummary }) =>
    showProductHeaderStickySummary ? "block" : "none"};
`;

const BreadCrumbContainerMobile = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  width: 100%;
`;

const StyledWebPageHeader = styled.View<{ isIFrame: boolean }>`
  padding: ${SpaceType.Double}px 48px;
  z-index: 999;
  ${({ isIFrame }) =>
    !isIFrame &&
    css`
      position: absolute;
    `}
`;

export default PDPScreen;
